import {
  Breadcrumbs,
  Button,
  Checkbox,
  Checklist,
  Field,
  FileField,
  Input,
  Loader,
  Tabs,
  Select
} from '@itds/elements/index.js';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ACTION_MESSAGES } from '@brokerage/utils/custom_hooks/useNotification';
import { Api } from '@brokerage/utils/Api';
import { MobileAppContext } from '@brokerage/contexts/MobileAppContext';

const Build = () => {
  const navigate = useNavigate();
  const params = useParams();

  // const [errorMessage, setErrorMessage] = useState(null);
  // const [successMessage, setSuccessMessage] = useState(null);
  const [fields, setFields] = useState([]);
  const [filtered_fields, setFilteredFields] = useState([]);
  const [current_tab, setTab] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState([]);
  const { setNotification } = useContext(MobileAppContext);

  useEffect(() => {
    const new_fields = fields.filter(field => {
      if (current_tab === 'Other') {
        return field.group === null || !field.group;
      }
      return field.group === current_tab;
    });

    setFilteredFields(new_fields);
  }, [fields, current_tab]);

  useEffect(() => {
    setLoading(true);
    Api.loadBuild(params.build_guid).then(response => {
      const { data } = response;
      const github_release_version = data.github_release_version;

      Api.loadConfigFields({ github_release_version }).then(({ data }) => {
        const tabs = [];

        // eslint-disable-next-line max-nested-callbacks
        data.forEach(field_data => {
          const tab = field_data.group === null || !field_data.group ? 'Other' : field_data.group;

          if (!tabs.includes(tab)) {
            tabs.push(tab);
          }
        });

        tabs.sort();

        setTabs(tabs);
        setFields(data);
        setTab(tabs[0]);
      });
    });

    loadConfigValues();
  }, [params.guid]);

  const loadConfigValues = () => {
    Api.loadConfigValues(params.build_guid).then(response => {
      const { data } = response;

      setValues(data);
      setLoading(false);
    });
  };

  const handleInputChange = (name, value, e) => {
    const new_values = values.filter(v => v.guid !== value.guid);

    new_values.push({ ...value, [name]: e.target.value });

    setValues(new_values);
  };

  const handleCheckboxChange = (value, e) => {
    const new_values = values.filter(v => v.guid !== value.guid);

    new_values.push({ ...value, boolean_value: e.target.checked });

    setValues(new_values);
  };

  const handleChecklistChange = (value, selected) => {
    const new_values = values.filter(v => v.guid !== value.guid);

    new_values.push({ ...value, array_values: selected });

    setValues(new_values);
  };

  const handleFileChange = (value, file) => {
    const new_values = values.filter(v => v.guid !== value.guid);

    new_values.push({ ...value, file_value: file });

    setValues(new_values);
  };

  const save = e => {
    e.preventDefault();
    setLoading(true);

    Api.updateBuildConfigValues(values).then(() => {
      loadConfigValues();
      setNotification(ACTION_MESSAGES.MOBILE_APPS.BUILD.SAVE);
    });
  };

  return (
    <div style={{ marginTop: -30 }}>
      <Breadcrumbs
        crumbs={[{ label: 'Builds', onClick: navigate.bind(null, -1) }]}
        current={params.guid}
      />

      {loading ? (
        <Loader />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {Object.keys(values).length ? (
            <>
              <Tabs
                tabs={tabs.map(t => ({
                  label: t,
                  isActive: t === current_tab,
                  onClick: setTab.bind(null, t)
                }))}
                vertical={true}
              />

              <form
                onSubmit={save}
                style={{ padding: '15px 40px', flex: 1, width: 'calc(100% - 200px)' }}
              >
                {filtered_fields.map((field, i) => {
                  const value = values.find(value => value.config_field_guid === field.guid) || {};

                  if (field.field_type === 'text') {
                    return (
                      <Field key={i} label={field.label}>
                        <Input
                          onChange={handleInputChange.bind(null, 'text_value', value)}
                          value={value.text_value || ''}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'textarea') {
                    return (
                      <Field key={i} label={field.label}>
                        <Input
                          height={120}
                          onChange={handleInputChange.bind(null, 'textarea_value', value)}
                          type='textarea'
                          value={value.textarea_value || ''}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'integer') {
                    return (
                      <Field key={i} label={field.label}>
                        <Input
                          onChange={handleInputChange.bind(null, 'integer_value', value)}
                          type='number'
                          value={value.integer_value || ''}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'decimal') {
                    return (
                      <Field key={i} label={field.label}>
                        <Input
                          onChange={handleInputChange.bind(null, 'decimal_value', value)}
                          step='0.01'
                          type='number'
                          value={value.decimal_value || ''}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'boolean') {
                    return (
                      <Field key={i}>
                        <Checkbox
                          checked={value.boolean_value || false}
                          label={field.label}
                          onChange={handleCheckboxChange.bind(null, value)}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'file') {
                    return (
                      <Field key={i} label={field.label}>
                        <FileField
                          file={value.file_value && value.file_value.url ? null : value.file_value}
                          fileUrl={
                            value.file_value && value.file_value.url ? value.file_value.url : ''
                          }
                          onChange={handleFileChange.bind(null, value)}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'array') {
                    return (
                      <Field key={i} label={field.label}>
                        <Checklist
                          onChange={handleChecklistChange.bind(null, value)}
                          options={field.array_options.map(o => ({ value: o }))}
                          selected={value.array_values || []}
                        />
                      </Field>
                    );
                  }

                  if (field.field_type === 'dropdown') {
                    return (
                      <Field key={i} label={field.label}>
                        <Select
                          onChange={handleInputChange.bind(null, 'text_value', value)}
                          options={field.array_options.map(o => ({ value: o, label: o }))}
                          value={value.text_value || ''}
                        />
                      </Field>
                    );
                  }

                  return null;
                })}
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 40 }}>
                  {Object.keys(values).length ? (
                    <Button onClick={save} type='submit'>
                      Save
                    </Button>
                  ) : null}
                </div>
              </form>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default Build;
